import { isIPadOSFrom, type PlatformProbe } from './iPadOS'

/**
 * WebKit-engine detection.
 *
 * Distinct from "is this Safari", which the codebase already answers twice —
 * `isSafari()` here in `DeviceManager` and `inSafari` in the web app — and both
 * answer it the same way, `window.safari !== undefined`. That global exists on
 * desktop Safari only, so both read false on iOS and iPadOS, and false on
 * Chrome or Edge for iOS.
 *
 * Those are the platforms this exists for. Every browser on iOS and iPadOS is
 * WebKit underneath whatever its User-Agent claims, so they all inherit
 * WebKit's media restrictions — including `setSinkId` rejecting with
 * `NotAllowedError` outside a transient user gesture, which is what callers
 * gate on. A predicate that missed them would leave the fault live for more
 * than half the users who hit it.
 *
 * Keep the Safari checks for questions about Safari the browser (its blur
 * support, its permissions copy). This one answers questions about the engine.
 */

/** The bits of the environment this probe reads. */
export interface WebKitProbe extends PlatformProbe {
  /** Whether `window.safari` exists — desktop Safari's own marker. */
  hasSafariGlobal: boolean
}

/** Pure resolver — takes the probe so it can be unit-tested without a DOM. */
export const isWebKitFrom = (probe: WebKitProbe): boolean => {
  if (probe.hasSafariGlobal) return true
  // An iPad reports a desktop-Macintosh UA, so touch points are what tell it
  // from the Mac it claims to be.
  if (isIPadOSFrom(probe)) return true
  // iPhone covers the iPod touch too: it reports `CPU iPhone OS` in the same
  // position, so a separate token would never be the thing that matched.
  if (/iPhone/.test(probe.userAgent)) return true
  return isMacSafariUserAgent(probe.userAgent)
}

/**
 * Desktop Safari by User-Agent, the fallback for when `window.safari` is
 * absent: extension sandboxes and some stripped iframe contexts drop it, and
 * nothing guarantees a future Safari keeps it. Without this a Mac Safari user
 * reads as not-WebKit and the dead-air ladder condemns them on the very
 * `sink_divergence` verdict the predicate exists to suppress.
 *
 * Every Chromium and Gecko build on macOS also prints `Safari/` in its UA, so
 * the token alone says nothing; it is the absence of the other engines' own
 * tokens that does. Scoped to `Macintosh` so an old Android stock browser,
 * which prints `Safari/` with no `Chrome`, is not swept in.
 */
const isMacSafariUserAgent = (ua: string): boolean =>
  /Macintosh/.test(ua) &&
  /Safari\//.test(ua) &&
  !/Chrome|Chromium|CriOS|Edg|Firefox|FxiOS|OPR/.test(ua)

/**
 * Whether the current environment runs on WebKit. False when there is no
 * `navigator` (tests, SSR) — callers treat that as "not WebKit" rather than a
 * third state, since every one of them gates a browser-only behaviour.
 */
export const isWebKit = (): boolean => {
  if (typeof navigator === 'undefined') return false
  return isWebKitFrom({
    userAgent: navigator.userAgent ?? '',
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    hasSafariGlobal:
      typeof window !== 'undefined' && window.safari !== undefined,
  })
}
